import {
  configureStore,
  combineReducers,
  Reducer,
  UnknownAction,
  Store,
} from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";
import { baseApi, apiMiddleware } from "./api";
import { analyticsMiddleware } from "./analyticsMiddleware";
import languageReducer from "../common/features/language/languageSlice";
import currencyReducer from "../common/features/currency/currencySlice";
import multiPanelOffcanvasReducer from "../common/features/multiPanelOffcanvas/multiPanelOffcanvasSlice";
import settingsPopupReducer from "../parts/footer/SettingsPopup/settingsPopupSlice";
import globalModalReducer from "../common/features/globalModal/globalModalSlice";
import productReducer from "../templates/ecommerce/product/productSlice";
import productModalReducer from "../templates/ecommerce/product/components/common/ProductModal/productModalSlice";
import checkoutReducer from "../templates/ecommerce/checkout/checkoutSlice";
import userReducer from "../templates/user/userSlice";
import globalPopupReducer from "../common/features/globalPopup/globalPopupSlice";
import cookieConsentReducer from "../common/features/cookieConsent/cookieConsentSlice";

// Define an interface for our extensible store, allowing for code splitting
export interface InjectedStore extends Store {
  asyncReducers?: { [key: string]: Reducer<any, UnknownAction> };
  injectReducer?: (key: string, asyncReducer: Reducer) => void;
}

// Define the reducers that are known at initialization time.
const staticReducers = {
  [baseApi.reducerPath]: baseApi.reducer,
  language: languageReducer,
  currency: currencyReducer,
  multiPanelOffcanvas: multiPanelOffcanvasReducer,
  settingsPopup: settingsPopupReducer,
  globalModal: globalModalReducer,
  product: productReducer,
  productModal: productModalReducer,
  checkout: checkoutReducer,
  user: userReducer,
  globalPopup: globalPopupReducer,
  cookieConsent: cookieConsentReducer,
};

// Create the main store configuration.
export const store: InjectedStore = configureStore({
  reducer: staticReducers,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(apiMiddleware).concat(analyticsMiddleware),
});

// Create a placeholder for async reducers that might be added later.
store.asyncReducers = {};

// Create the injection function and attach it to the store instance.
// This allows other parts of the app to inject their own reducers as needed.
store.injectReducer = (key, asyncReducer) => {
  if (!store.asyncReducers) {
    store.asyncReducers = {};
  }
  store.asyncReducers[key] = asyncReducer;

  // Combine the static and newly injected reducers to create a new root reducer.
  const newRootReducer = combineReducers({
    ...staticReducers,
    ...store.asyncReducers,
  });

  store.replaceReducer(newRootReducer);
};

// Set up listeners for RTK Query to enable caching, invalidation, polling, etc.
setupListeners(store.dispatch);

// Infer the `RootState` and `AppDispatch` types from the store itself for TypeScript.
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
